import { useMemo } from 'react';
import type { OccurrenceStepEnum } from '../../../enums/occurrenceStep';
import { useKanbanCharts } from '../../../hooks/useKanban';
import type { OccurrenceWithTimeSpent } from '../../../models/occurrence';
import type { Step } from '../../../models/step';
import { toStepValue } from '../trackingUtils';
import type { StageSummary } from '../types';

interface ITrackingSummaryInput {
    totalOccurrences: number;
    totalInProgress: number;
    currentList: OccurrenceWithTimeSpent[];
    stageList: OccurrenceWithTimeSpent[];
    currentStageMode: boolean;
    finalizedList: OccurrenceWithTimeSpent[];
    finishedFilter: string[];
    stepFilter: OccurrenceStepEnum | null;
    chassisListFilterByStep: string[];
    stepKeys: string[];
    steps: Step[];
}

const useTrackingSummary = ({
    totalOccurrences,
    totalInProgress,
    currentList,
    stageList,
    currentStageMode,
    finalizedList,
    finishedFilter,
    stepFilter,
    chassisListFilterByStep,
    stepKeys,
    steps,
}: ITrackingSummaryInput) => {
    const { getBigNumbers } = useKanbanCharts({
        totalOccurrences,
        totalInProgress,
        occurrenceWithTimeSpentList: currentList,
        finalizedOccurrenceWithTimeSpentList: finalizedList,
        finishedFilter,
        stepFilter,
        chassisListFilterByStep,
    });
    const bigNumbers = getBigNumbers();

    const estimateByStep = useMemo(
        () =>
            new Map(steps.map(step => [step.id, step.elapsedHours > 0 ? step.elapsedHours * 3600 * 1000 : undefined])),
        [steps]
    );

    const lateCount = useMemo(
        () =>
            currentList.filter(item => {
                const estimate = item.currentStep ? estimateByStep.get(item.currentStep) : undefined;
                return estimate !== undefined && item.currentStepTotalTime * 60 * 1000 > estimate;
            }).length,
        [currentList, estimateByStep]
    );

    const stages = useMemo<StageSummary[]>(
        () =>
            stepKeys.map(stepKey => {
                const step = toStepValue(stepKey);
                const inStep = stageList.filter(item => item.currentStep === step);
                const withStep = currentStageMode ? inStep : stageList.filter(item => item.steps[step]);
                const totalSeconds = withStep.reduce((sum, item) => sum + (item.steps[step]?.total ?? 0), 0);
                const chassisCount = new Set(withStep.map(item => item.chassis)).size;
                return {
                    stepKey,
                    step,
                    averageMs: chassisCount ? (totalSeconds / chassisCount) * 1000 : undefined,
                    estimateMs: estimateByStep.get(step),
                    currentCount: inStep.length,
                    count: withStep.length,
                };
            }),
        [stepKeys, stageList, currentStageMode, estimateByStep]
    );

    return { bigNumbers, lateCount, stages };
};

export default useTrackingSummary;
